import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { adminHubAuth } from './config/firebase.js';
import { buildApiUrl } from './utils/apiBase.js';
import {
  signOutEverywhere,
  restoreAdminHubAuthFromSession,
  getFirebaseSignInProvider,
  isCustomTokenSignIn,
  isGoogleSignInProvider,
} from './utils/authSession.js';
import {
  resetAuthBootstrap,
  runAuthBootstrap,
  registerUnauthorizedHandler,
  runWithoutUnauthorizedHandler,
} from './utils/authController.js';
import { startDashboardPrefetch, resetDashboardPrefetch } from './utils/dashboardPrefetch.js';
import { shouldLoadDashboardSummary } from './config/adminPermissions.js';
import { bootMark, bootMarkOnce, bootStart, bootEnd, bootSubtotal } from './utils/bootTiming.js';

import AdminDashboard from './components/admin/AdminDashboard.jsx';
import AdminLoginPage from './components/admin/AdminLoginPage.jsx';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';

const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();

  if (loading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" />
        <p>読み込み中...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const GuestRoute = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="app-loading">
        <LoadingSpinner size="large" />
        <p>読み込み中...</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

function AppContent() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [bootstrapVersion, setBootstrapVersion] = useState(0);
  const userRef = useRef(null);
  const authReadyRef = useRef(false);
  const sessionSyncRef = useRef(false);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const applySessionUser = useCallback((sessionUser) => {
    const next = {
      uid: sessionUser.uid,
      email: sessionUser.email,
      userId: sessionUser.userId || '',
      displayName: sessionUser.displayName,
      role: sessionUser.role,
      hubRole: sessionUser.hubRole,
      permissions: sessionUser.permissions,
      clinicId: sessionUser.clinicId,
      isActive: sessionUser.isActive,
      avatarUrl: sessionUser.avatarUrl
    };
    // setState 反映前に onAuthStateChanged が走るため、userRef は同期更新する。
    // 早期ゲート開放後の Custom Token 復元で uid 不一致と誤判定し signOut するのを防ぐ。
    userRef.current = next;
    setUser(next);
  }, []);

  const clearSession = useCallback(async ({ message = '' } = {}) => {
    userRef.current = null;
    setUser(null);
    setAuthError(message);
    resetDashboardPrefetch();
    resetAuthBootstrap();
    authReadyRef.current = false;
    sessionSyncRef.current = false;
    await runWithoutUnauthorizedHandler(() => signOutEverywhere());
    setLoading(false);
    setBootstrapVersion((v) => v + 1);
  }, []);

  const establishSessionFromIdToken = useCallback(async (idToken) => {
    const response = await fetch(buildApiUrl('/api/create-session'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
      credentials: 'include'
    });

    const errData = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(errData.message || 'セッションの作成に失敗しました');
    }

    if (!errData?.user || errData.user.role !== 'admin') {
      throw new Error('管理者として認証できませんでした');
    }

    applySessionUser(errData.user);
    if (shouldLoadDashboardSummary(errData.user)) {
      startDashboardPrefetch();
    }
    return errData;
  }, [applySessionUser]);

  const handleFirebaseUser = useCallback(async (authUser) => {
    if (!authReadyRef.current) return;

    if (!authUser) {
      if (!userRef.current) {
        setLoading(false);
      }
      return;
    }

    if (sessionSyncRef.current) return;

    const provider = await getFirebaseSignInProvider(authUser);

    // Custom Token は Session Cookie 復元用。create-session は Google ID Token 専用
    if (isCustomTokenSignIn(provider)) {
      if (userRef.current?.uid === authUser.uid) {
        setLoading(false);
        return;
      }
      await adminHubAuth.signOut().catch(() => {});
      setLoading(false);
      return;
    }

    if (!isGoogleSignInProvider(provider)) {
      await clearSession({ message: '許可されていない認証方式です。Google でログインしてください。' });
      navigate('/login', { replace: true });
      return;
    }

    if (userRef.current?.uid === authUser.uid) {
      setLoading(false);
      return;
    }

    sessionSyncRef.current = true;
    setLoading(true);
    setAuthError('');

    try {
      const idToken = await authUser.getIdToken();
      await establishSessionFromIdToken(idToken);
    } catch (error) {
      console.error('認証処理エラー:', error);
      await clearSession({ message: error.message || 'ログインに失敗しました' });
      navigate('/login', { replace: true });
    } finally {
      sessionSyncRef.current = false;
      setLoading(false);
    }
  }, [clearSession, establishSessionFromIdToken, navigate]);

  useEffect(() => {
    registerUnauthorizedHandler(async () => {
      if (!userRef.current) return;
      await clearSession({ message: 'セッションの有効期限が切れました。再度ログインしてください。' });
      navigate('/login', { replace: true });
    });
    return () => registerUnauthorizedHandler(null);
  }, [clearSession, navigate]);

  useEffect(() => {
    let cancelled = false;
    // 既にセッションユーザーがいる再実行では loading を立てない。
    // setLoading(true) は ProtectedRoute 経由で AdminDashboard をアンマウントし、
    // 再マウント時に初回ロードが欠落するとシマーが残るため。
    if (!userRef.current) {
      authReadyRef.current = false;
      setLoading(true);
    }
    bootMarkOnce('app:auth-bootstrap-effect');

    const unsubscribe = onAuthStateChanged(adminHubAuth, (authUser) => {
      handleFirebaseUser(authUser);
    });

    // 共有 Promise 内では effect ローカルの cancelled を見ない。
    // cleanup で reset すると verify が重複し、cancelled 付きクロージャが
    // applySessionUser をスキップしてしまう（Dev/本番共通のレース）。
    runAuthBootstrap(async () => {
      bootStart('auth:verify-session');
      try {
        const response = await fetch(buildApiUrl('/api/verify-session'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        if (!response.ok) {
          bootEnd('auth:verify-session', `HTTP ${response.status}`);
          return null;
        }

        const sessionData = await response.json();
        bootEnd('auth:verify-session', sessionData.valid ? 'valid' : 'invalid');
        return sessionData;
      } catch (err) {
        bootEnd('auth:verify-session', `error: ${err?.message || 'unknown'}`);
        console.log('[Session] 起動時復元スキップ:', err?.message);
        return null;
      }
    }).then(async (sessionData) => {
      if (cancelled) return;
      if (sessionData?.valid && sessionData.user?.role === 'admin') {
        applySessionUser(sessionData.user);
        bootMark('auth:session-applied');
        if (shouldLoadDashboardSummary(sessionData.user)) {
          startDashboardPrefetch();
        }

        // API は Session Cookie で足りる。Firebase 復元完了を待たずゲートを開き、
        // ダッシュボード（シマー）表示を約1秒早められる。
        authReadyRef.current = true;
        setLoading(false);
        bootMarkOnce('auth:gate-open');
        bootSubtotal('認証ゲート開放（スピナー解除）');

        bootStart('auth:restore-firebase');
        await restoreAdminHubAuthFromSession();
        bootEnd('auth:restore-firebase');
      }
    }).finally(async () => {
      if (cancelled) return;
      if (!authReadyRef.current) {
        authReadyRef.current = true;
        setLoading(false);
        bootMarkOnce('auth:gate-open');
        bootSubtotal('認証ゲート開放（スピナー解除）');
      }
      await handleFirebaseUser(adminHubAuth.currentUser);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      // resetAuthBootstrap はログアウト時のみ。cleanup では共有 in-flight を維持する。
    };
  }, [applySessionUser, handleFirebaseUser, bootstrapVersion]);

  useEffect(() => {
    if (!user) return undefined;

    const refreshSession = async () => {
      try {
        const authUser = adminHubAuth.currentUser;
        if (!authUser) return;
        const provider = await getFirebaseSignInProvider(authUser);
        if (!isGoogleSignInProvider(provider)) return;

        const idToken = await authUser.getIdToken();
        const response = await fetch(buildApiUrl('/api/refresh-session'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ idToken })
        });
        if (response.ok) {
          const data = await response.json();
          if (data.user?.role === 'admin') {
            applySessionUser(data.user);
          }
        } else if (response.status === 401) {
          await clearSession({ message: 'セッションの有効期限が切れました。再度ログインしてください。' });
          navigate('/login', { replace: true });
        }
      } catch {
        // バックグラウンド更新 — 失敗しても UI は維持
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [user, applySessionUser, clearSession, navigate]);

  const handleLogout = useCallback(async () => {
    resetDashboardPrefetch();
    await clearSession();
    navigate('/login', { replace: true });
  }, [clearSession, navigate]);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <GuestRoute user={user} loading={loading}>
            <AdminLoginPage
              authError={authError}
              onClearAuthError={() => setAuthError('')}
            />
          </GuestRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute user={user} loading={loading}>
            <AdminDashboard user={user} onLogout={handleLogout} />
          </ProtectedRoute>
        }
      />

      <Route
        path="/"
        element={
          loading ? (
            <div className="app-loading">
              <LoadingSpinner size="large" />
              <p>読み込み中...</p>
            </div>
          ) : (
            <Navigate to={user ? '/admin' : '/login'} replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
